import React from "react";
import HeroSlider from "../components/UI/HeroSlider";
import Helmet from "../components/Helmet/Helmet";
import { Container, Row, Col } from "reactstrap";
import FindCarForm from "../components/UI/FindCarForm";
import AboutSection from "../components/UI/AboutSection";
import ServicesList from "../components/UI/ServicesList";
import BecomeDriverSection from "../components/UI/BecomeDriverSection";
import BlogList from "../components/UI/BlogList";
import { Phone, MessageCircle, ChevronDown, Star } from 'lucide-react';
import slide1 from "../assets/all-images/slider-img/slide1.png";
import slide3 from "../assets/all-images/slider-img/slide3.png";
import slide7 from "../assets/all-images/slider-img/slide7.jpg";

const Home = () => {
  // Services data for the new section
  const services = [
    {
      id: 1,
      title: "Rent a Car",
      image: slide1,
      description: "Premium vehicles for every occasion. Whether business or leisure, we have the perfect ride for you."
    },
   
    {
      id: 3,
      title: "Car Clinic",
      image: slide3,
      description: "Professional detailing and wash services. Experience the ultimate shine with our expert care and attention."
    },
   
    {
      id: 5,
      title: "Barbershop",
      image: slide7,
      description: "Classic grooming with modern style. Expert cuts, beard sculpting, and premium grooming services."
    }
  ];

  const faqs = [
    {
      q: "What services does Dabridge offer?",
      a: "Dabridge is your one-stop solution for car rental, fine dining, professional car cleaning, premium water delivery, and expert barbershop services."
    },
    {
      q: "How can I place an order or make a booking?",
      a: "You can easily reach us via phone call or WhatsApp using the buttons on our service cards, or click the floating WhatsApp widget on any page."
    },
    {
      q: "Do you offer delivery services?",
      a: "Yes! We offer delivery for our water products and can arrange pickup/drop-off for car rentals. Contact us for specific service availability."
    },
    {
      q: "What are your operating hours?",
      a: "Operating hours vary by service. Please contact us directly via WhatsApp or phone for specific timing and availability."
    }
  ];

  const testimonials = [
    {
      name: "Patricia N.",
      text: "Dabridge has become my go-to for everything! From renting a car for my business trips to getting fresh water delivered, they never disappoint.",
      rating: 5
    },
    {
      name: "David M.",
      text: "The restaurant is amazing and the car wash service is top-notch. It's so convenient having all these quality services in one place.",
      rating: 5
    },
    {
      name: "Elizabeth K.",
      text: "Professional service across the board. The barbershop gave me the best haircut I've had in years, and their attention to detail is remarkable.",
      rating: 5
    }
  ];

  const handleCall = () => {
    window.location.href = "tel:0720333127";
  };

  const handleWhatsApp = () => {
    window.open("https://wa.me/254795906060", "_blank");
  };

  return (
    <Helmet title="Home">
      <style>{`
        /* Service Cards Styling */
        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2.5rem;
          margin-top: 2rem;
        }

        .service-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .service-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 35px rgba(0, 13, 107, 0.15);
        }

        .service-card-image {
          width: 100%;
          height: 240px;
          object-fit: cover;
        }

        .service-card-content {
          padding: 2rem;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }

        .service-card-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #000D6B;
          margin-bottom: 1rem;
          text-align: center;
        }

        .service-card-description {
          color: #64748b;
          line-height: 1.7;
          margin-bottom: 2rem;
          text-align: center;
          flex-grow: 1;
        }

        .service-card-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .service-btn {
          padding: 14px 20px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: opacity 0.2s, transform 0.2s;
          border: none;
          text-decoration: none;
          color: white;
        }

        .service-btn:hover {
          opacity: 0.9;
          transform: translateY(-2px);
        }

        .service-btn-call {
          background-color: #000D6B;
        }

        .service-btn-whatsapp {
          background-color: #f9a826;
        }

        /* FAQ & Testimonials Styling */
        .info-section {
          background: white;
          padding: 5rem 0;
          border-top: 1px solid #e2e8f0;
        }

        .split-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
        }

        .faq-side, .testimonial-side {
          display: flex;
          flex-direction: column;
        }

        .subsection-header {
          text-align: left;
          margin-bottom: 2rem;
        }

        .subsection-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #000D6B;
          margin-bottom: 0.5rem;
        }

        .subsection-desc {
          font-size: 1rem;
          color: #64748b;
        }

        .faq-item {
          margin-bottom: 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          padding-bottom: 1.5rem;
        }

        .faq-q {
          font-weight: 700;
          color: #000D6B;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.8rem;
          font-size: 1rem;
        }

        .faq-a {
          color: #64748b;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        .testimonial-card {
          background: #f8fafc;
          padding: 2rem;
          border-radius: 16px;
          margin-bottom: 1.5rem;
          border: 1px solid #eef2f6;
        }

        .testimonial-card p {
          font-style: italic;
          color: #475569;
          margin: 0 0 1rem 0;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        .testimonial-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .testimonial-author {
          font-weight: 700;
          font-size: 0.9rem;
          color: #000D6B;
        }

        .stars {
          display: flex;
          gap: 2px;
        }

        /* WhatsApp Widget */
        .whatsapp-widget {
          position: fixed;
          bottom: 30px;
          right: 30px;
          background: #25D366;
          color: white;
          padding: 14px 24px;
          border-radius: 50px;
          box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          z-index: 9999;
          transition: transform 0.2s, box-shadow 0.2s;
          text-decoration: none;
          font-weight: 600;
          font-size: 15px;
        }

        .whatsapp-widget:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 30px rgba(37, 211, 102, 0.5);
          color: white;
        }

        .whatsapp-icon {
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .services-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .split-layout {
            gap: 3rem;
          }
        }

        @media (max-width: 768px) {
          .services-grid {
            grid-template-columns: 1fr;
          }

          .split-layout {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .service-card-actions {
            grid-template-columns: 1fr;
          }

          .whatsapp-widget {
            bottom: 20px;
            right: 20px;
            padding: 12px 20px;
            font-size: 14px;
          }
        }

        @media (max-width: 480px) {
          .whatsapp-widget span {
            display: none;
          }

          .whatsapp-widget {
            padding: 14px;
            border-radius: 50%;
          }
        }
      `}</style>

      {/* ============= hero section =========== */}
      <section className="p-0 hero__slider-section">
        <HeroSlider />

        <div className="hero__form">
          <Container>
            <Row className="form__row">
              <Col lg="4" md="4">
                <div className="find__cars-left">
                  <h2>Find your best car here</h2>
                </div>
              </Col>

              <Col lg="8" md="8" sm="12">
                <FindCarForm />
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      {/* =========== about section ================ */}
      <AboutSection />

      {/* ========== services section ============ */}
      <section>
        <Container>
          <Row>
            <Col lg="12" className="mb-5 text-center">
              <h6 className="section__subtitle">See our</h6>
              <h2 className="section__title">Popular Services</h2>
            </Col>

            <ServicesList />
          </Row>
        </Container>
      </section>

      {/* =========== NEW: Our Services section (replacing car offer) ============= */}
      <section>
        <Container>
          <Row>
            <Col lg="12" className="text-center mb-5">
              <h6 className="section__subtitle">Discover</h6>
              <h2 className="section__title">Our Premium Services</h2>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <div className="services-grid">
                {services.map((service) => (
                  <div key={service.id} className="service-card">
                    <img src={service.image} alt={service.title} className="service-card-image" />
                    <div className="service-card-content">
                      <h3 className="service-card-title">{service.title}</h3>
                      <p className="service-card-description">{service.description}</p>
                      <div className="service-card-actions">
                        <button className="service-btn service-btn-call" onClick={handleCall}>
                          <Phone size={16} /> Request Call
                        </button>
                        <button className="service-btn service-btn-whatsapp" onClick={handleWhatsApp}>
                          <MessageCircle size={16} /> Order Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* =========== become a driver section ============ */}
      <BecomeDriverSection />

      {/* =========== NEW: FAQ & Testimonials section (replacing old testimonial) =========== */}
      <section className="info-section">
        <Container>
          <Row>
            <Col lg="12" className="mb-4 text-center">
              <h6 className="section__subtitle">What people say</h6>
              <h2 className="section__title">FAQs & Testimonials</h2>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <div className="split-layout">
                {/* FAQs */}
                <div className="faq-side">
                  <div className="subsection-header">
                    <h2 className="subsection-title">Common Questions</h2>
                    <p className="subsection-desc">Everything you need to know about our services</p>
                  </div>
                  {faqs.map((item, i) => (
                    <div key={i} className="faq-item">
                      <div className="faq-q">
                        {item.q}
                        <ChevronDown size={18} color="#f9a826" />
                      </div>
                      <div className="faq-a">{item.a}</div>
                    </div>
                  ))}
                </div>

                {/* Testimonials */}
                <div className="testimonial-side">
                  <div className="subsection-header">
                    <h2 className="subsection-title">Happy Customers</h2>
                    <p className="subsection-desc">Don't just take our word for it</p>
                  </div>
                  {testimonials.map((t, i) => (
                    <div key={i} className="testimonial-card">
                      <p>"{t.text}"</p>
                      <div className="testimonial-footer">
                        <span className="testimonial-author">— {t.name}</span>
                        <div className="stars">
                          {[...Array(t.rating)].map((_, idx) => (
                            <Star key={idx} size={14} fill="#f9a826" color="none" />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* =============== blog section =========== */}
      <section>
        <Container>
          <Row>
            <Col lg="12" className="mb-5 text-center">
              <h6 className="section__subtitle">Explore our blogs</h6>
              <h2 className="section__title">Latest Blogs</h2>
            </Col>

            <BlogList />
          </Row>
        </Container>
      </section>

      {/* WhatsApp Widget */}
      <a
        href="https://wa.me/254795906060"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-widget"
      >
        <MessageCircle size={24} className="whatsapp-icon" />
        <span>Chat with Dabridge</span>
      </a>
    </Helmet>
  );
};

export default Home;